/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../ts-components/Text'
import { Organization } from '@sportninja/common/types/Organization'
import { Team } from '@sportninja/common/types/TeamStanding'
import { Schedule } from '@sportninja/common/types/Schedule'
import {
  TargetAudienceSelector,
  EntityDisplay,
  FeedPostModalIconButtons,
} from './'

interface PostHeaderProps {
  selfUser: any
  entity?: any
  entityType: string
  orgs: Organization[]
  teams: Team[]
  scheds: Schedule[]
  selected?: string
  setSelected: (value: any) => void
  selectedType: string | null
  setSelectedType: (value: string | null) => void
  targetAudience: number | null
  setTargetAudience: (value: number | null) => void
  setParentId: (value: string) => void
  setParentEntityType: (
    value: 'team' | 'schedule' | 'organization' | undefined
  ) => void
  isLoading: boolean
  postToEdit: any
  isLoadingEntities: boolean
  showImage: boolean
  showLink: boolean
  showSendVia: boolean
  onToggleSection: (section: 'image' | 'link' | 'sendVia') => void
}

/**
 * PostHeader component - displays user avatar, name, and target audience selector
 *
 * This component handles the top section of the post creation modal, including:
 * - User avatar and name display
 * - Target audience selection (organization/team/schedule dropdown)
 * - Modal icon buttons for toggling different sections
 *
 * @param props - Component props for user data, entity selection, and UI controls
 * @returns JSX element representing the post header section
 */
export const PostHeader: React.FC<PostHeaderProps> = ({
  selfUser,
  entity,
  entityType,
  orgs,
  teams,
  scheds,
  selected,
  setSelected,
  selectedType,
  setSelectedType,
  targetAudience,
  setTargetAudience,
  setParentId,
  setParentEntityType,
  isLoading,
  postToEdit,
  isLoadingEntities,
  showImage,
  showLink,
  showSendVia,
  onToggleSection,
}) => {
  console.log('🔄 PostHeader: Rendering component')

  return (
    <div
      css={css`
        align-self: stretch;
        justify-content: flex-start;
        align-items: flex-start;
        gap: 8px;
        display: flex;
        margin-bottom: 8px;
      `}
    >
      <div
        css={css`
          flex: 1 1 0;
          justify-content: flex-start;
          align-items: center;
          gap: 8px;
          display: flex;
        `}
      >
        <img
          css={css`
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: 1px white solid;
            object-fit: contain;
          `}
          src={
            selfUser?.imageId ||
            '/images/generic_placeholders/placeholder_player.png'
          }
          alt='User Avatar'
        />
        <div
          css={css`
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            align-items: flex-start;
          `}
        >
          <div
            css={css`
              justify-content: center;
              align-items: center;
              gap: 4px;
              display: flex;
            `}
          >
            <Text
              variant='paragraphMedium'
              weight='bold'
              color={colors.NEUTRAL_0}
            >
              {selfUser?.fullName}
            </Text>
          </div>
          <div
            css={css`
              margin-top: 4px;
              width: 100%;
              display: flex;
              align-items: center;
              gap: 8px;
            `}
          >
            <Text
              variant='paragraphXSmall'
              weight='regular'
              color={colors.NEUTRAL_50}
            >
              Post To:
            </Text>
            {entity ? (
              <EntityDisplay entity={entity} entityType={entityType} />
            ) : (
              <TargetAudienceSelector
                orgs={orgs}
                teams={teams}
                scheds={scheds}
                selected={selected}
                setSelected={setSelected}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                targetAudience={targetAudience}
                setTargetAudience={setTargetAudience}
                setParentId={setParentId}
                setParentEntityType={setParentEntityType}
                disabled={isLoading || postToEdit !== null || isLoadingEntities}
              />
            )}
          </div>
        </div>
      </div>
      <FeedPostModalIconButtons
        showImage={showImage}
        showLink={showLink}
        onToggle={onToggleSection}
        sendViaSelected={showSendVia}
        onSendViaClick={() => onToggleSection('sendVia')}
      />
    </div>
  )
}
